import { History, Location } from 'history';
import { isAuthenticated } from 'mk/bazaar/common/userUtils';
import BlogCommentAdd from 'mk2/apps/blogs/components/BlogCommentAdd';
import { CommentList } from 'mk2/apps/blogs/components/CommentList';
import { CommentListPlaceholder } from 'mk2/apps/blogs/components/CommentListPlaceholder';
import { CommentsDesign } from 'mk2/apps/blogs/constants';
import {
    commentsFetchTrigger,
    commentCreateTrigger,
    commentCreateUnauthenticatedTrigger,
    commentDeleteTrigger,
    commentReply,
    commentToggleHighlight,
} from 'mk2/apps/blogs/containers/Comments/Comments.actions';
import { CommentsState } from 'mk2/apps/blogs/containers/Comments/Comments.reducers';
import { getCommentsNonDeleted, getCommentsState } from 'mk2/apps/blogs/containers/Comments/Comments.selectors';
import { getCommentParent, BLOG_POST_COMMENT_MENU_MORE, COMMENT_TEXTAREA_FORM_NAME } from 'mk2/apps/blogs/helpers';
import { postSendHeart } from 'mk2/apps/forum/actions';
import { CommentFormData } from 'mk2/apps/forum/components/CommentCreateEditForm';
import { LoadError } from 'mk2/components/errors/LoadError';
import { complaintOpenTrigger } from 'mk2/containers/Complaint/Complaint.actions';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import { getComplaintsShownForBlogComments } from 'mk2/containers/Complaints/Complaints.selectors';
import { getDeletionsShownForBlogComments } from 'mk2/containers/Deletion/Deletion.selectors';
import { menuToggle } from 'mk2/containers/Menu/Menu.actions';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { getRequestUser, AppState } from 'mk2/reducers';
import { isFailure, isLoading, isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { BlogPostEntity, CommentEntity, CommentParent, CommentSchema, ImageEntity, UserEntity } from 'mk2/schemas';
import { getDenormalizedEntities } from 'mk2/selectors';
import { subscribeJam, unsubscribeJam } from 'mk2/services/jam';
import React from 'react';
import { connect } from 'react-redux';

interface OwnProps {
    // commentare mozu patrit:
    // 1. postu (vtedy parentPost !== null && parentImage == null)
    // 2. fotke (vtedy parentPost !== null && parentImage !== null)
    parentPost: BlogPostEntity;
    parentImage?: ImageEntity;

    count: number;
    design: CommentsDesign;
    isPreloaded?: boolean;
    hasBlueToolbar?: boolean;
    moderators?: UserEntity[];
    location: Location;
    history: History;
    showFakeTextareaIfCantAdd?: boolean;
    defaultCanAdd?: boolean;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    isMobile: boolean;

    onBlur?();
    onFocus?();
    onSetTextareaRef?(ref: HTMLTextAreaElement);
}

interface StateProps {
    commentsParent: CommentParent;
    comments: CommentEntity[];
    commentToBeHighlighted: number | 'last';
    loadingState: LoadingState;
    canAdd: boolean;
    hasHiddenText: boolean;
    complaintsOpened: boolean[];
    deletionsOpened: boolean[];
    createFormName: string;
}

interface DispatchProps {
    onLoad(commentParent: CommentParent);
    onCreate(commentParent: CommentParent, commentFd: CommentFormData);
    onCreateUnauthenticated(commentParent: CommentParent);
    onDelete(commentParent: CommentParent, commentId: number);
    onReport(entityType: ComplaintTargetType, commentId: number);
    onReply(commentParent: CommentParent, username: string, textareaElem: HTMLTextAreaElement, repliedToCommentId: number);
    onToggleMenuMore(commentId: number);
    onToggleHighlight(commentParent: CommentParent, commentId: number);
    onSendHeart();
}

type Props = OwnProps & StateProps & DispatchProps;

class Comments extends React.PureComponent<Props> {

    private messageFormTextarea: HTMLTextAreaElement;

    public componentDidMount() {
        const { commentsParent } = this.props;

        // do not need to call onLoad(), initial data are passed from django via html
        subscribeJam(`comment:${commentsParent.model}:${commentsParent.id}`);
    }

    public componentWillUnmount() {
        const { commentsParent } = this.props;

        // do not need to call onLoad(), initial data are passed from django via html
        unsubscribeJam(`comment:${commentsParent.model}:${commentsParent.id}`);
    }

    public render() {
        const {
            loadingState, comments, requestUser, requestUserPermissions, isMobile, parentPost, location,
            design, count, complaintsOpened, canAdd, hasHiddenText, createFormName, deletionsOpened,
            commentToBeHighlighted, history, moderators, showFakeTextareaIfCantAdd, hasBlueToolbar,
            onReport, onSendHeart, onToggleMenuMore, onBlur, onFocus,
        } = this.props;

        const hash = location.hash;
        const hashValue = hash && hash.indexOf('-') !== -1 ? hash.split('-')[1] : null;
        const toBeHighlightedFromHash = hashValue === 'last' && comments && comments.length > 0 ? comments[comments.length - 1].id : parseFloat(hashValue);
        const toBeHighlightedFromState = commentToBeHighlighted && (commentToBeHighlighted === 'last' ?
                (comments && comments.length > 0 && comments[comments.length - 1].id) :
                commentToBeHighlighted
        );
        let highlightedId = toBeHighlightedFromState ? toBeHighlightedFromState : toBeHighlightedFromHash;
        if (isNaN(highlightedId)) {
            highlightedId = null;
        }

        const hasData = comments !== undefined && comments !== null;

        let commentsList;
        if (isLoading(loadingState) && !hasData) {
            commentsList = (
                <CommentListPlaceholder
                    commentsCount={count}
                    design={design}
                />
            );
        } else if (isFailure(loadingState) && !hasData) {
            commentsList = (
                <LoadError onRetry={this.load} changeTitle={false} />
            );
        } else if (isSuccess(loadingState) || hasData) {
            commentsList = (
                <CommentList
                    comments={comments}
                    commentsCount={count}
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                    hasHiddenText={hasHiddenText}
                    parentPost={parentPost}
                    highlightTriggeredId={highlightedId}
                    highlightStartedId={toBeHighlightedFromState}
                    isMobile={isMobile}
                    moderators={moderators}
                    design={design}
                    complaintsOpened={complaintsOpened}
                    deletionsOpened={deletionsOpened}
                    location={location}
                    history={history}
                    onDidHighlight={this.handleOnDidHighlight}
                    onReply={this.handleOnReply}
                    onDelete={this.handleOnDelete}
                    onReport={onReport}
                    onSendHeart={onSendHeart}
                    onToggleMenuMore={onToggleMenuMore}
                />
            );
        } else {
            // should not happen
            return null;
        }

        return (
            <>
                {commentsList}

                <BlogCommentAdd
                    requestUser={requestUser}
                    requestUserPermissions={requestUserPermissions}
                    isMobile={isMobile}
                    canAdd={canAdd}
                    hasBlueToolbar={hasBlueToolbar}
                    showFakeTextareaIfCantAdd={showFakeTextareaIfCantAdd}
                    createFormName={createFormName}
                    design={design}
                    onSetTextareaRef={this.handleSetMessageFormTextareaRef}
                    onCreate={this.handleOnCreate}
                    onCreateUnauthenticated={this.handleOnCreateUnauthenticated}
                    onBlur={onBlur}
                    onFocus={onFocus}
                />
            </>
        );
    }

    public UNSAFE_componentWillMount() {
        const { comments, count, isPreloaded } = this.props;
        if (!isPreloaded && (count === 0 || !Boolean(comments) || comments.length !== count)) {
            this.load();
        }
    }

    private load = () => {
        const { onLoad, commentsParent } = this.props;
        onLoad(commentsParent);
    };

    private handleOnDidHighlight = (commentId: number) => {
        const { onToggleHighlight, commentsParent } = this.props;
        onToggleHighlight(commentsParent, commentId);
    };

    private handleOnCreate = (commentFd: CommentFormData) => {
        const { onCreate, commentsParent } = this.props;
        onCreate(commentsParent, commentFd);
    };

    private handleOnReply = (username: string, repliedToCommentId: number) => {
        const { onReply, commentsParent } = this.props;
        onReply(commentsParent, username, this.messageFormTextarea, repliedToCommentId);
    };

    private handleOnDelete = (commentId: number) => {
        const { onDelete, commentsParent } = this.props;
        onDelete(commentsParent, commentId);
    };

    private handleOnCreateUnauthenticated = () => {
        const { onCreateUnauthenticated, commentsParent } = this.props;
        onCreateUnauthenticated(commentsParent);
    };

    private handleSetMessageFormTextareaRef = (ref: HTMLTextAreaElement) => {
        const { onSetTextareaRef } = this.props;

        this.messageFormTextarea = ref;
        if (onSetTextareaRef) {
            onSetTextareaRef(ref);
        }
    };
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    const { parentPost, parentImage, defaultCanAdd } = ownProps;

    const commentsParent = getCommentParent(parentPost, parentImage);
    const commentsState: CommentsState = getCommentsState(state, commentsParent);
    const commentsAll = commentsState.ids
        ? getDenormalizedEntities<CommentEntity>(state, CommentSchema, commentsState.ids)
        : null;
    const comments = commentsState.ids
        ? ownProps.design === CommentsDesign.Normal ? commentsAll : getCommentsNonDeleted(commentsAll)
        : null;

    const canAdd = commentsState.loadingState !== LoadingState.SUCCESS && defaultCanAdd !== undefined
        ? defaultCanAdd
        : isAuthenticated(getRequestUser(state)) && commentsState.canAdd;
    const createFormName = COMMENT_TEXTAREA_FORM_NAME(commentsParent);
    return {
        commentsParent,
        comments,
        commentToBeHighlighted: commentsState.commentToBeHighlighted,
        loadingState: commentsState.loadingState,
        // Never allow canAdd for non-authenticated users
        canAdd,
        hasHiddenText: commentsState.hasHiddenText,
        createFormName,
        complaintsOpened: getComplaintsShownForBlogComments(state, comments || []),
        deletionsOpened: getDeletionsShownForBlogComments(state, comments || []),
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onLoad: commentsFetchTrigger,
    onCreate: commentCreateTrigger,
    onCreateUnauthenticated: commentCreateUnauthenticatedTrigger,
    onDelete: commentDeleteTrigger,
    onReply: commentReply,
    onReport: complaintOpenTrigger,
    onToggleMenuMore: (commentId: number) => menuToggle(BLOG_POST_COMMENT_MENU_MORE(commentId)),
    onToggleHighlight: commentToggleHighlight,
    onSendHeart: postSendHeart,
};

export default connect(mapStateToProps, mapDispatchToProps)(Comments);
